import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import ModulesDailyLotto from './modules/dailyLotto/index';
import ModulesInstantLotto from './modules/instant/index';
import { LOTTO_INSTANT } from '@/constants/lotto/instant-lotto';
import { KEY_INFO } from '@/constants/store';

interface Tab {
    key: string;
    label: string;
}

export default createStore({
    state: {
        config: {},
        configDevice: {},
        listBluetooth: [],
        versionApp: '1.1.0',
        userInfo: null,
        token: '',
        tokenV3: '',
        lang: 'fr',
        isBluetooth: false,
        defaultPrinter: '',
        isScreenPhone: false,
        isScreenMobile: false,
        isLoadingAddCart: false,
        increaseFontSize: '',
        isCheckFontSize: false,
        isCheckTicketCode: false,
        tabActive: 'okapi lottery',
        millisecondsToForceFinishPrinting: 2000,
        reprintCountToShowCancel: 3,
        cart: {
            dataTransact: [],
            dataRender: [],
            quantity: 0,
            games: null,
            events: null,
        },
        lotto: {
            is_show_loto_lucky5: false,
            is_show_loto_lucky4: false,
            is_show_loto_lucky3: false,
            minimumIncrement: 100,
        },
        betSlip: {
            active: '',
            nav: [],
            isNav: false,
            okapiLottery: false,
            dailyLotto: false,
            instantLotto: false,
        },
        tabLotto: {
            list: [] as Tab[],
        },
        isReprintDialogShowing: false,
    },
    getters: {
        getConfigOnFeature: (state: any) => (page: string) => {
            const allConfig = { ...state.config };

            return {
                ...allConfig.features.find((c: any) => c.name.toLowerCase().includes(page.toLowerCase())),
            };
        },
    },
    mutations: {
        SET_CONFIG(state, config) {
            state.config = config;
            state.lotto.is_show_loto_lucky5 = config?.enabled_game_slugs?.includes('5-90-lucky5') || false;
            state.lotto.is_show_loto_lucky4 = config?.enabled_game_slugs?.includes('5-90-lucky4') || false;
            state.lotto.is_show_loto_lucky3 = config?.enabled_game_slugs?.includes('5-90-lucky3') || false;

            state.lotto.minimumIncrement = config?.setup?.minimumIncrement || 100;

            state.millisecondsToForceFinishPrinting = config.setup?.millisecondsToForceFinishPrinting || 2000;
            state.reprintCountToShowCancel = config.setup?.reprintCountToShowCancel || 3;
            state.isCheckFontSize = config.setup?.isCheckFontSize || false;
            state.isCheckTicketCode = config.setup?.isCheckTicketCode || false;

            const defaultTabResult: Tab[] = [
                {
                    key: 'okapiLottery',
                    label: 'okapi lottery',
                },
            ];

            state.tabLotto.list = config?.tabResult || defaultTabResult;

            const features = [...config?.features];

            const indexOkapiLottery = features.findIndex((x) => x.routing === '/loto-590');
            const indexDailyLotto = features.findIndex((x) => x.routing === '/lotto-fixed-daily-lotto');
            const indexInstantLotto = features.findIndex((x) => x.name === LOTTO_INSTANT.SLUG_API);

            state.betSlip.okapiLottery = indexOkapiLottery !== -1;
            state.betSlip.dailyLotto = indexDailyLotto !== -1;
            state.betSlip.instantLotto = indexInstantLotto !== -1;

            const conditions = [state.betSlip.okapiLottery, state.betSlip.dailyLotto, state.betSlip.instantLotto];

            state.betSlip.isNav = conditions.filter(Boolean).length >= 2;
        },
        SET_CONFIG_DEVICE(state, config) {
            state.configDevice = config;
        },
        SET_VERSION_APP(state, version) {
            state.versionApp = version;
        },
        SET_LIST_BLUETOOTH(state, data) {
            state.listBluetooth = data;
        },
        SET_IS_BLUETOOTH(state, param) {
            state.defaultPrinter = param.id;
            state.isBluetooth = param.isBluetooth;
        },
        SET_LANG(state, lang) {
            state.lang = lang;
        },
        SET_LOGIN(state, payload) {
            state.userInfo = payload.userInfo;
            state.token = payload.token;
            state.tokenV3 = payload.tokenV3;
        },
        SET_INCREASE_FONTSIZE(state, keyIncreaseFontSize) {
            state.increaseFontSize = keyIncreaseFontSize;
        },
        SET_LOGOUT(state) {
            state.userInfo = null;
            state.token = '';
            state.tokenV3 = '';
            state.cart.dataTransact = [];
            state.cart.dataRender = [];
            state.cart.quantity = 0;
            state.cart.games = null;
            state.cart.events = null;
        },
        SET_GAME_SELECTED(state, data) {
            state.cart.games = data;
        },
        SET_EVENT_SELECTED(state, data) {
            state.cart.events = data;
        },
        SET_ADD_CART(state, payLoadAddCart) {
            state.cart.dataRender = payLoadAddCart.dataRender;
            state.cart.dataTransact = payLoadAddCart.dataTransact;
            state.cart.quantity = payLoadAddCart.quantity;
            state.isLoadingAddCart = false;
        },
        SET_CART_EVENT_EMPTY(state) {
            state.cart.events = null;
        },
        SET_CART_GAME_EMPTY(state) {
            state.cart.games = null;
        },
        SET_IS_REPRINT_SHOW(state, isReprint) {
            state.isReprintDialogShowing = isReprint;
        },
        SET_TAB_ACTIVE(state, type) {
            state.betSlip.active = type;
        },
    },
    actions: {
        setLogin({ commit }, payload) {
            const dataIncreaseFontSize = payload.userInfo?.details?.cashier_setting?.value;

            const isIncreaseFontSize = dataIncreaseFontSize
                ? dataIncreaseFontSize[KEY_INFO.VALUE_LARGE_INCREASE_FONTSIZE]
                    ? KEY_INFO.VALUE_LARGE_INCREASE_FONTSIZE
                    : KEY_INFO.VALUE_NORMAL_INCREASE_FONTSIZE
                : KEY_INFO.VALUE_NORMAL_INCREASE_FONTSIZE;

            commit('SET_INCREASE_FONTSIZE', isIncreaseFontSize);
            commit('SET_LOGIN', payload);
        },

        setIsReprintShow({ commit }, payload) {
            commit('SET_IS_REPRINT_SHOW', payload);
        },

        setTabActive({ commit }, type) {
            commit('SET_TAB_ACTIVE', type);
        },

        addToCart({ commit, state }, payload) {
            const dataRender: any = { ...state.cart.dataRender };
            const dataTransact: any = { ...state.cart.dataTransact };
            let quantity: number = state.cart.quantity;
            payload.events_selected.map((x: any) => {
                const item = {
                    ...payload,
                    id: new Date()
                        .getTime()
                        .toString(36)
                        .concat(performance.now().toString(), Math.random().toString())
                        .replace(/\./g, ''),
                    parentId: payload.id,
                    events: x,
                };

                if (!dataRender[payload.type]) {
                    dataRender[payload.type] = [];
                }

                dataRender[payload.type].push(item);
            });

            if (!dataTransact[payload.type]) {
                dataTransact[payload.type] = [];
            }
            dataTransact[payload.type].push({ ...payload });

            quantity += payload.events_selected.length;

            const payLoadAddCart = {
                dataTransact,
                dataRender,
                quantity,
            };
            commit('SET_ADD_CART', payLoadAddCart);
        },

        onRemoveItemCart({ commit, state }, payload) {
            const { item, type } = payload;
            const dataRender: any = { ...state.cart.dataRender };
            const dataTransact: any = { ...state.cart.dataTransact };
            let quantity: number = state.cart.quantity;

            const indexDataRender = dataRender[type].findIndex((x: any) => x.id === item.id);
            const indexDataTransact = dataTransact[type].findIndex((x: any) => x.id === item.parentId);

            if (indexDataRender !== -1) {
                dataRender[type].splice(indexDataRender, 1);
            }

            if (indexDataTransact !== -1) {
                const elTransact = dataTransact[type][indexDataTransact];
                const indexEvent = elTransact.events_selected.findIndex(
                    (x: any) => x.event_id === item.events.event_id,
                );
                if (indexEvent !== -1) {
                    if (elTransact.events_selected.length === 1) dataTransact[type].splice(indexDataTransact, 1);
                    else elTransact.events_selected.splice(indexEvent, 1);
                }
            }

            quantity -= 1;
            const payLoadAddCart = {
                dataTransact,
                dataRender,
                quantity,
            };
            commit('SET_ADD_CART', payLoadAddCart);
        },

        onClearCart({ commit, state }, type) {
            const dataRender: any = state.cart.dataRender;
            const dataTransact: any = state.cart.dataTransact;
            let quantity: number = state.cart.quantity;

            quantity -= dataRender[type].length;

            dataRender[type] = [];
            dataTransact[type] = [];

            const payLoadAddCart = {
                dataTransact,
                dataRender,
                quantity,
            };
            commit('SET_ADD_CART', payLoadAddCart);
        },
    },
    modules: {
        ModulesDailyLotto,
        ModulesInstantLotto,
    },
    plugins: [createPersistedState()],
});
